/**
 * Notes tab — the visit notes already in the patient's chart, plus the entry
 * point for drafting a new one.
 *
 * This replaces the read-out half of the retired Encounter tab (ACT-1241/1242).
 * It deliberately does NOT reuse `ElationEncounterPanel`: that component still
 * carries the per-section write-back grid (its own `WriteBackCard` /
 * `SectionWriteList`), which is exactly the tech-demo UI that
 * `@astrana/ui`'s one-review-one-write card replaced. Re-surfacing that panel
 * wholesale would put the deprecated flow back in front of clinicians. This is
 * read-only by construction — it renders chart notes and nothing else writes
 * from here.
 *
 * "Create note" does not mount its own scribe session. Mounting a second
 * `AmbientScribePanel` would give the two entry points separate sessions and
 * let a stale draft from one survive the other (see the note above the Scribe
 * tab in MemberProfilePage). Instead the host switches to the Scribe tab, so
 * there is exactly one session for the whole profile.
 */
import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, FilePlus2 } from "lucide-react";

import { useElationBridge } from "~/bridge/useElationBridge";
import type { ElationNote } from "~/bridge/types";
import { humanTemplateName } from "@ext/elation/elationChartNotes";

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">{children}</div>
  );
}

function Badge({ children, tone = "muted" }: { children: React.ReactNode; tone?: "muted" | "open" }) {
  const cls =
    tone === "open"
      ? "bg-primary/10 text-primary"
      : "bg-muted text-muted-foreground";
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{children}</span>
  );
}

/**
 * What the row's badge says. A draft must READ as a draft: before the chart
 * notes came from Elation's own model, an unsigned note could only reach this
 * panel by being opened, so everything that wasn't the open note was labelled
 * "Signed" — including drafts, once they were listed at all.
 *
 * `noteStatus` carries the finer state (it can say "on-hold", which `status`
 * cannot); `status` is the fallback for feed-scraped notes that have no
 * `noteStatus`.
 */
function noteBadgeLabel(note: ElationNote): string {
  if (note.status === "open") return "Open";
  switch (note.noteStatus ?? note.status) {
    case "signed":
      return "Signed";
    case "on-hold":
      return "On hold";
    default:
      return "Draft";
  }
}

/** A labelled block, omitted entirely when the chart has nothing for it. */
function Block({ label, value }: { label: string; value: string }) {
  if (!value || !value.trim()) return null;
  return (
    <div className="mt-3">
      <p className="m-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="m-0 mt-1 whitespace-pre-wrap text-sm">{value.trim()}</p>
    </div>
  );
}

function Vitals({ vitals }: { vitals: Record<string, string> }) {
  const entries = Object.entries(vitals ?? {}).filter(([, v]) => v && v.trim());
  if (entries.length === 0) return null;
  return (
    <div className="mt-3">
      <p className="m-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Vitals
      </p>
      <div className="mt-1 flex flex-wrap gap-2">
        {entries.map(([name, value]) => (
          <span key={name} className="rounded-lg bg-muted px-2 py-1 text-xs">
            <span className="text-muted-foreground">{name}: </span>
            {value.trim()}
          </span>
        ))}
      </div>
    </div>
  );
}

function NoteBody({ note }: { note: ElationNote }) {
  const hasAnything = [
    note.examReason,
    note.subjective,
    note.objective,
    note.assessment,
    note.plan,
    note.ros,
    note.pe,
    note.followUp,
  ].some((v) => v && v.trim());

  return (
    <div className="border-t border-border px-4 pb-4">
      {!hasAnything && Object.keys(note.vitals ?? {}).length === 0 ? (
        <p className="m-0 mt-3 text-sm text-muted-foreground">
          This note has no readable content in the chart.
        </p>
      ) : (
        <>
          <Block label="Reason" value={note.examReason} />
          <Block label="Subjective" value={note.subjective} />
          <Block label="Objective" value={note.objective} />
          <Block label="Assessment" value={note.assessment} />
          <Block label="Plan" value={note.plan} />
          <Block label="Review of systems" value={note.ros} />
          <Block label="Physical exam" value={note.pe} />
          <Block label="Follow-up" value={note.followUp} />
          <Vitals vitals={note.vitals} />
        </>
      )}
    </div>
  );
}

function NoteRow({
  note,
  defaultOpen,
  unverified = false,
}: {
  note: ElationNote;
  defaultOpen: boolean;
  /**
   * True when this row rode the DOM-feed fallback (`chartNotesStatus:
   * "unverified"`): real chart rows, but the feed scrape carries no patient
   * attribution, so in an in-tab switch window they can still be the previous
   * patient's. The row says so — subdued, not alarming — because rendering it
   * identically to a patient-guarded row quietly re-promotes the one
   * unattributed source in the system back to fact.
   */
  unverified?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const isOpenNote = note.status === "open";
  // A REAL template name ("SOAP Note") is the most specific thing we know, so it
  // still wins — but it must be a NAME and not Elation's layout class, which is
  // what `getTemplate()` actually returns live ("oneCol.oneField").
  //
  // GUARDED ON BOTH FIELDS, and `examReason` last. The read sets `visitType`
  // from the template name too, so guarding only `templateName` still let
  // "oneCol.oneField" through here — which is exactly what happened when the
  // extension was running an older build of the injected read than the web app.
  // `examReason` carries the note's DESCRIPTION ("Office Visit Note"), so the
  // heading stays correct even against a stale extension.
  const heading =
    humanTemplateName(note.templateName) ||
    humanTemplateName(note.visitType) ||
    note.examReason?.trim() ||
    "Visit note";
  const subtitle =
    [note.visitDate?.trim(), note.provider?.trim()].filter(Boolean).join(" · ") ||
    "Date unknown";

  return (
    <div className="overflow-hidden rounded-xl border border-border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/50"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        )}
        <span className="flex-1">
          <span className="block text-sm font-medium">{heading}</span>
          <span className="block text-xs text-muted-foreground">{subtitle}</span>
        </span>
        {unverified && (
          <span
            title="Read from the rendered chart view; the patient-verified read has not answered yet. Refreshes automatically."
            className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground"
          >
            Unconfirmed source
          </span>
        )}
        <Badge tone={isOpenNote ? "open" : "muted"}>{noteBadgeLabel(note)}</Badge>
      </button>
      {open && <NoteBody note={note} />}
    </div>
  );
}

export interface ChartNotesPanelProps {
  /**
   * Take the clinician to the drafting flow. The host owns this so the scribe
   * session stays single — see the file header.
   */
  onCreateNote?: () => void;
}

/**
 * How long the panel lets an unresolved read stay "Still reading…" before it
 * escalates to the could-not-read state. The extension retries on every poll
 * (up to 10s apart) and on every tab/URL event, so two full poll cycles with no
 * answer means the read is not merely racing Elation's navigation — it is not
 * happening (blocked injection, a chart layout with no `el8`, an extension too
 * old to report a status at all).
 */
export const CHART_NOTES_READ_FAILURE_AFTER_MS = 30_000;

export function ChartNotesPanel({ onCreateNote }: ChartNotesPanelProps) {
  const { data, clinical } = useElationBridge();

  const openNote = clinical?.openNote ?? null;
  // `chartNotes` is the chart's FULL note list — signed notes plus the unsigned
  // drafts / on-hold notes, read from Elation's own model. `signedNotes` (the
  // chart-feed DOM scrape, signed-only by construction) is the fallback for a
  // bridge that hasn't sent the new field, and for a page where the model read
  // was unavailable.
  const listedNotes = clinical?.chartNotes ?? clinical?.signedNotes ?? [];
  // Never list the open note twice. The list read and the open-note scrape
  // refresh on different cadences, so between them the note the clinician just
  // opened can legitimately appear in both.
  const openNoteId = openNote?.noteId;
  const history = openNoteId
    ? listedNotes.filter((n) => n.noteId !== openNoteId)
    : listedNotes;
  // The open note first — it is the one the clinician is working in — then the
  // rest of the chart, which the bridge already returns most-recent-first.
  const notes: ElationNote[] = [...(openNote ? [openNote] : []), ...history];

  // AN EMPTY LIST IS NOT ALWAYS AN EMPTY CHART.
  //
  // Reported from QA: open a chart with 5-6 signed notes, then navigate the
  // Elation tab to another patient, and every note vanished — replaced by "No
  // visit notes in this chart yet." That sentence is a clinical claim about the
  // new patient, and in that window we had no basis for it: the extension had
  // already re-read the chart for the new patient and BOTH of its sources were
  // legitimately empty (the `el8` model read is unavailable while Elation
  // navigates, and the chart feed renders asynchronously — see
  // `elationChartNotes.ts`). `chartNotes` was `[]` for "no notes" and for "no
  // read" alike, so the panel could only ever pick one sentence for both.
  //
  // `chartNotesStatus` is that missing bit, computed where both sources are in
  // hand, and "loaded" is the ONLY value licensed to render the empty state — it
  // is the one case where a patient-guarded read answered and the answer was
  // "this chart has no notes". Everything else fails closed:
  //
  //  - "unread" — the read has not answered (mid-navigation, or not yet).
  //  - ABSENT — an extension too old to carry the field, which is the exact
  //    can't-tell-empty-from-unread state this field was added to end. Claiming
  //    "no visit notes" off it is the vanish bug verbatim for every user whose
  //    extension has not updated, so it gets the same treatment as "unread".
  //  - anything unrecognised (a newer extension) — unknown never widens a claim.
  //
  // Deliberately not "keep showing the last list": the previous patient's notes
  // under this patient's chart is the worse failure of the two.
  const emptyIsFact = clinical?.chartNotesStatus === "loaded";

  // A FAILED READ MUST BECOME VISIBLE. Without this, a read that can never
  // succeed — blocked MAIN-world injection, a chart layout with no `el8`, an
  // old extension that reports nothing — renders as "Still reading…" forever: a
  // permanent progress claim that hides the one signal a clinician or support
  // could act on. The extension cannot say "failed" itself (a single tick cannot
  // tell a navigation race from a dead read, and an old extension says nothing
  // at all), so the panel owns the escalation: an unresolved empty list that has
  // outlived every retry cadence stops claiming progress and says what is
  // actually known — the notes could not be read. It never claims the chart is
  // empty; the wording carries the reason and what keeps being tried.
  //
  // The timer restarts when the patient changes (a fresh chart deserves a fresh
  // reading window) and clears the moment the read resolves either way.
  const unresolved = notes.length === 0 && !emptyIsFact;
  const patientKey = data?.ehrPatientId ?? data?.patientName ?? "";
  const [readTimedOut, setReadTimedOut] = useState(false);
  useEffect(() => {
    setReadTimedOut(false);
    if (!unresolved) return;
    const timer = setTimeout(
      () => setReadTimedOut(true),
      CHART_NOTES_READ_FAILURE_AFTER_MS,
    );
    return () => clearTimeout(timer);
  }, [unresolved, patientKey]);

  // No chart in context at all. Distinct from "a chart with no notes". (After
  // the hooks — hooks must run unconditionally.)
  if (!clinical && !data) {
    return (
      <Card>
        <p className="m-0 text-sm text-muted-foreground">
          No Elation patient detected. Open a patient&apos;s chart in Elation to see their
          visit notes here.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="m-0 text-base font-semibold">Visit notes</h2>
            <p className="m-0 mt-1 text-sm text-muted-foreground">
              {notes.length > 0
                ? `${notes.length} note${notes.length === 1 ? "" : "s"} from the chart.`
                : emptyIsFact
                  ? "No visit notes in this chart yet."
                  : readTimedOut
                    ? // The terminal state for a read that is not going to
                      // succeed on its own. Claims nothing about the patient —
                      // "couldn't confirm" is the strongest statement the
                      // evidence supports — and names the two recoveries that
                      // actually fix the known causes (a chart Elation has not
                      // finished rendering, and an out-of-date extension).
                      "Couldn’t confirm this chart’s visit notes from Elation. The list keeps refreshing — if this persists, reopen the chart in Elation and check that the Atlas Copilot extension is up to date."
                    : // Says what we know and what we are doing about it, and
                      // claims nothing about the patient. The read is retried on
                      // every poll and on every tab/URL event, so "refreshes
                      // automatically" is a statement about the extension's
                      // actual behaviour. A read that keeps not answering does
                      // not sit on this line forever — see `readTimedOut`.
                      "Still reading this chart’s visit notes from Elation — this list refreshes automatically."}
            </p>
          </div>
          {onCreateNote && (
            <button
              type="button"
              onClick={onCreateNote}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              <FilePlus2 className="h-4 w-4" aria-hidden="true" />
              Create note
            </button>
          )}
        </div>
      </Card>

      {notes.length > 0 && (
        <div className="space-y-2">
          {notes.map((note, i) => (
            <NoteRow
              // Model-read notes carry their real `noteId`; feed-scraped ones
              // (the fallback path) don't, so those stay keyed positionally
              // within a stable, most-recent-first list.
              key={note.noteId ?? `${note.status}-${note.visitDate}-${i}`}
              note={note}
              // The whole LIST is what the status describes, so every
              // feed-fallback row carries the mark — except the open note,
              // which is scraped live from the note the clinician is editing
              // and never rides the chart-list read.
              unverified={
                clinical?.chartNotesStatus === "unverified" && note.status !== "open"
              }
              // Only the open note expands by default: it is the one in play,
              // and expanding a long signed history buries the Create button.
              defaultOpen={i === 0 && note.status === "open"}
            />
          ))}
        </div>
      )}
    </div>
  );
}
